import type { ScpData } from "@hobbyshopify/backend-client";
import { useEffect, useState } from "react";
import { GRADE_CATEGORIES, gradePriceText } from "../core/primitives/grades";

// eBay grade links + per-grade prices, ported from the iOS CardDetailView
// (category selector + grade-button grid). Prices come from `scp_data`
// (SportsCardsPro, in cents); links from the `search_links` map. Buttons
// without a link render dimmed/non-clickable, mirroring `ebayButtonColor`.
//
// The taxonomy and price mapping live in lib/grades, shared with the
// manual-grade picker in ScanEditForm.

// Opened verbatim: scan-core already appends the affiliate params when it builds
// `search_links` (see its pure/ebay-links.ts + constants.ts). Re-appending them
// here duplicated every mkcid/mkevt/campid pair, and eBay answers a link with
// duplicated tracking params by redirecting to sign-in — which is why these
// worked in the iOS app (it opens the URL as-is) but not on web.
function openLink(url?: string) {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
}

export function EbayGradeLinks({
  searchLinks,
  scpData,
  pricedGradeKey,
  cardGradeKey,
}: {
  searchLinks?: Record<string, string>;
  scpData?: ScpData | null;
  /**
   * The grade the asking price was read from — `estimatedPriceGrade`, the
   * backend's own answer rather than a guess made from the amounts here.
   *
   * Marked because the estimate is often *not* this card's grade: SCP prices
   * PSA 1–10 and the top SGC/CGC grade, so a BGS slab or a half grade is read
   * off the PSA comp below it. A reviewer looking at a grid of twenty numbers
   * cannot tell which one became the figure in the form, and that is exactly
   * the thing they are being asked to approve.
   */
  pricedGradeKey?: string | null;
  /**
   * This card's own grade, as a key — `raw` for an ungraded card.
   *
   * Separate from `pricedGradeKey` because the two answer different questions
   * and only agree sometimes: a BGS 9.5 is priced off the PSA 9 comp, and an
   * ungraded card with no loose price on SCP is priced off nothing at all. That
   * second case is why this exists — `pricedGradeKey` is null there, so a raw
   * card marked nothing and the selector fell back to reading PSA as current.
   */
  cardGradeKey?: string | null;
}) {
  // Open on the category holding the priced grade, so the marked button is on
  // screen without hunting for it.
  const [selected, setSelected] = useState(
    () => GRADE_CATEGORIES.find((c) => c.grades.some((g) => g.key === pricedGradeKey))?.id ?? "psa",
  );

  // And follow it when it moves: correcting the grade to a BGS one while the
  // grid stays on the PSA tab would hide the mark behind a tab nobody has a
  // reason to open. A tab the reviewer chose themselves is left alone — this
  // only fires when the priced grade actually changes.
  useEffect(() => {
    const category = GRADE_CATEGORIES.find((c) => c.grades.some((g) => g.key === pricedGradeKey));
    if (category) setSelected(category.id);
  }, [pricedGradeKey]);

  // RAW has no button in the grid below, so the tab is the only place an
  // ungraded card can be marked at all.
  const rawIsThisCard = cardGradeKey === "raw" || pricedGradeKey === "raw";

  const rawUrl = searchLinks?.raw;
  const rawPriceText = gradePriceText("raw", scpData);
  const activeCategory = GRADE_CATEGORIES.find((c) => c.id === selected) ?? GRADE_CATEGORIES[0];

  const tabBase = "flex-1 rounded-md px-2 py-1.5 text-center transition-colors disabled:cursor-default";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-white">eBay sold prices &amp; links</span>
        <span className="text-[10px] text-faint">
          {pricedGradeKey
            ? "Highlighted grade is the one this card is priced from"
            : rawIsThisCard
              ? "This card is ungraded"
              : "Tap a grade to view comps on eBay"}
        </span>
      </div>

      {/* Category selector — RAW links straight out, the rest swap the grid below. */}
      <div className="flex rounded-btn bg-surface p-1 text-xs font-medium">
        <button
          type="button"
          disabled={!rawUrl}
          onClick={() => openLink(rawUrl)}
          // RAW has no button in the grid below, so an ungraded card's price can
          // only be marked here.
          className={`${tabBase} ${
            rawIsThisCard ? "bg-accent/10 text-accent" : rawUrl ? "text-white hover:bg-surface-strong" : "text-faint"
          }`}
        >
          <span className="block">RAW</span>
          {rawPriceText && <span className="block text-[10px] font-normal">{rawPriceText}</span>}
        </button>
        {GRADE_CATEGORIES.map((cat) => {
          const isSelected = cat.id === selected;
          const priceText = cat.priceKey ? gradePriceText(cat.priceKey, scpData) : "";
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelected(cat.id)}
              className={`${tabBase} ${isSelected ? "bg-surface-strong text-accent" : "text-muted hover:text-white"}`}
            >
              <span className="block">{cat.title}</span>
              {priceText && <span className="block text-[10px] font-normal">{priceText}</span>}
            </button>
          );
        })}
      </div>

      {/* Grade grid for the selected category. */}
      <div className="grid grid-cols-3 gap-2">
        {activeCategory.grades.map((grade) => {
          const url = searchLinks?.[grade.key];
          const priceText = gradePriceText(grade.key, scpData);
          const priced = grade.key === pricedGradeKey;
          return (
            <button
              key={grade.key}
              type="button"
              disabled={!url}
              onClick={() => openLink(url)}
              // iOS fills a grade button with the accent when it is the active
              // one; here the accent is the hover/hit affordance instead, since
              // a web grid has no persistent selection to mark — except for the
              // priced grade, which is a fact about this scan rather than a
              // pointer, and keeps the accent outright.
              className={`rounded-btn border px-2 py-2 text-xs font-medium transition-colors ${
                priced
                  ? "border-accent bg-accent/10 text-accent"
                  : url
                    ? "border-line text-white hover:border-accent hover:text-accent"
                    : "cursor-default border-line text-faint opacity-50"
              }`}
            >
              <span className="block">{grade.label}</span>
              {priceText && <span className="block text-[11px] font-normal">{priceText}</span>}
              {priced && <span className="block text-[10px] font-normal opacity-80">Price from here</span>}
            </button>
          );
        })}
      </div>

      <p className="text-[10px] italic text-faint">
        As an eBay Partner, we may earn commissions from purchases made through these links.
      </p>
    </div>
  );
}
